import { DataTable, DataTableColumnDef } from "@shared/EDS/components/DataTable/DataTable";
import { ScenarioBorderTrain } from "@opstel/query/v2/models/scenarioBorderTrain";
import { ScenarioBorderSelection as ScenarioBorderSelectionData } from "@opstel/query/v2/models/scenarioBorderSelection";
import { useMemo, useState } from "react";
import { TractieSelect } from "@tractie/components/react/TractieSelect";
import { Track } from "@opstel/query/models/track";
import { inputValueChangeHandler } from "@shared/lion";
import { DateTime } from "luxon";
import { ElementOf } from "@shared/common/types/typeHelpers";
import { RadioPanel, RadioPanelItem } from "@shared/EDS/components/RadioPanel/RadioPanel";
import { DATETIME_FORMAT_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { Toolbar } from "@shared/EDS/components/Toolbar";

export interface ScenarioBorderSelectionProps {
  readonly appearingScenarioBorderTrains: ScenarioBorderTrain[];
  readonly disappearingScenarioBorderTrains: ScenarioBorderTrain[];
  readonly scenarioBorderSelections: ScenarioBorderSelectionData[];
  readonly tracks: Track[];
  readonly scenarioStart?: DateTime<true>;
  readonly scenarioEnd?: DateTime<true>;
  readonly isLoading?: boolean;
  readonly updateScenarioBorderSelection: UpdateScenarioBorderSelectionStateFn;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}
export type UpdateScenarioBorderSelectionStateFn = (params: ScenarioBorderSelectionData) => void;

export type CombinedData = ScenarioBorderTrain & ScenarioBorderSelectionData;

const SCENARIO_BORDER = ["START", "END"] as const;
type ScenarioBorderType = ElementOf<typeof SCENARIO_BORDER>;

function combineWithScenarioBorderSelection(
  borderTrains: ScenarioBorderTrain[],
  scenarioBorderSelections: ScenarioBorderSelectionData[],
  tracks: Track[],
): CombinedData[] {
  return borderTrains.map((borderTrain) => {
    const scenarioBorderSelection =
      scenarioBorderSelections.find((s) => s.scenarioTrainId === borderTrain.id) ??
      ({
        scenarioTrainId: borderTrain.id,
        scenarioBorderTrack:
          tracks.find((track) => track.name === borderTrain.track)?.code ?? borderTrain.track,
      } satisfies ScenarioBorderSelectionData);
    return { ...borderTrain, ...scenarioBorderSelection };
  });
}

interface ScenarioBorderSelectFieldProps {
  readonly value?: string;
  readonly tracks: Track[];
  readonly onChange: (value: string) => void;
}

function ScenarioBorderSelectField({ value, tracks, onChange }: ScenarioBorderSelectFieldProps) {
  return (
    <TractieSelect
      modelValue={value}
      onModelValueChanged={inputValueChangeHandler<string>(onChange)}
    >
      <select slot="input" aria-label="Spoor scenariogrens" defaultValue={value ?? ""}>
        {tracks.map(({ code }) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
    </TractieSelect>
  );
}

interface ScenarioBorderTrackCellProps {
  readonly original: CombinedData | undefined;
  readonly tracks: Track[];
  readonly updateScenarioBorderSelection: UpdateScenarioBorderSelectionStateFn;
}

function ScenarioBorderTrackCell({
  original,
  tracks,
  updateScenarioBorderSelection,
}: ScenarioBorderTrackCellProps) {
  if (!original) return null;
  return (
    <ScenarioBorderSelectField
      value={original.scenarioBorderTrack}
      tracks={tracks}
      onChange={(input) => {
        if (input === original.scenarioBorderTrack) return;
        updateScenarioBorderSelection({
          ...original,
          scenarioBorderTrack: input,
        });
      }}
    />
  );
}

export function SelectScenarioBorders({
  appearingScenarioBorderTrains,
  disappearingScenarioBorderTrains,
  scenarioBorderSelections,
  tracks,
  isLoading = false,
  updateScenarioBorderSelection,
  onSubmit,
  onClose,
  scenarioStart,
  scenarioEnd,
}: ScenarioBorderSelectionProps) {
  const [selectedFilter, setSelectedFilter] = useState<ScenarioBorderType>("START");

  const borderTrains = useMemo(() => {
    if (selectedFilter === "START") {
      return appearingScenarioBorderTrains;
    }
    return disappearingScenarioBorderTrains;
  }, [appearingScenarioBorderTrains, disappearingScenarioBorderTrains, selectedFilter]);

  const combinedData = useMemo(
    () => combineWithScenarioBorderSelection(borderTrains, scenarioBorderSelections, tracks),
    [borderTrains, scenarioBorderSelections, tracks],
  );

  const filterOptions: RadioPanelItem<ScenarioBorderType>[] = [
    { label: "Scenario start", value: "START" },
    { label: "Scenario eind", value: "END" },
  ];

  const columns: DataTableColumnDef<CombinedData>[] = useMemo(
    () => [
      {
        id: "borderTrains",
        columns: [
          {
            id: "trainName",
            header: "Treinnummer",
            accessorFn: (row) => row.name,
          },
          {
            id: "trainUnitTypes",
            header: "Materieel",
            accessorFn: (row) => row.trainUnitTypes,
          },
          {
            id: "scenarioBorderTrack",
            header: "Spoor scenariogrens",
            accessorFn: (row) => row.scenarioBorderTrack,
            cell: ({ row }) => (
              <ScenarioBorderTrackCell
                original={row.original}
                tracks={tracks}
                updateScenarioBorderSelection={updateScenarioBorderSelection}
              />
            ),
          },
        ],
        meta: { className: "table-header-emphasized" },
      },
    ],
    [tracks, updateScenarioBorderSelection],
  );

  return (
    <>
      <tractie-helptext>
        Scenario: {scenarioStart?.toFormat(DATETIME_FORMAT_NO_SECONDS) ?? "?"} t/m{" "}
        {scenarioEnd?.toFormat(DATETIME_FORMAT_NO_SECONDS) ?? "?"}
      </tractie-helptext>
      <Toolbar>
        <div className="mt-m flex w-full items-center justify-between">
          <RadioPanel items={filterOptions} onChange={setSelectedFilter} defaultValue="START" />
        </div>
      </Toolbar>
      <div style={{ height: "60vh", overflowY: "auto" }}>
        <DataTable
          className="scenario-border-selection-table"
          columns={columns}
          data={combinedData}
          defaultSort={[{ id: "trainName", desc: false }]}
          options={{
            isRowInteractive: () => false,
            emptyMessage: "Geen scenariogrenzen beschikbaar.",
            loadingMessage: "Scenariogrenzen worden geladen...",
            getRowId: (row) => row.scenarioTrainId,
          }}
          isLoading={isLoading}
        />
      </div>
      <footer slot="end" style={{ textAlign: "right" }}>
        <tractie-button level="tertiary" onClick={onClose}>
          Annuleren
        </tractie-button>
        <tractie-button level="primary" onClick={onSubmit}>
          Opslaan
        </tractie-button>
      </footer>
    </>
  );
}
